import CopyToClipboardIcon from '@components/Icon/Copy';
import EyeIcon from '@components/Icon/Eye';
import JobIcon from '@components/Icon/Job';
import MapMarker from '@components/Icon/MapMarker';
import { DataTable, EmptyState, useContainerHeight } from '@components/DataTable';
import { DataTableColumn, SortModel } from '@components/DataTable/types';
import formatNumberToK from '@helper/formatNumbertoK';
import { JobApplicationStatus } from '@modules/jobApplications/statuses';
import JobOfferRowActions from '@modules/JobOffers/components/JobOfferRowActions';
import { AssignJobOfferDialog } from '@modules/JobOffers/components/AssignJobOfferDialog';
import { Avatar, AvatarGroup, Box, Collapse, IconButton, Skeleton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAssigneeDisplayInfo } from './utils/assignee-utils';

interface JobOffer {
    _id: string;
    imageUrl: string;
    slug: string;
    fullName: string;
    jobOffer: string;
    city: string;
    rating: number;
    origin: string;
    totalApplied: number;
    appliedJobs: number;
    experience: string;
    contractType: string;
    education: string;
    company: string;
    logo: string;
    active: boolean;
    status?: string;
    publicLink?: string;
    title: string;
    jobNumber?: string;
    pro?: {
        _id: string;
        commercialName?: string;
        city?: string;
        brandLogo?: {
            url: string
        };
    };
    brand?: {
        brandLogo?: {
            url: string
        };
    };
    contract?: {
        contractType?: string;
        guaranteedSalary?: {
            min?: number;
            max?: number;
        };
        isAnnualSalary?: boolean;
    };
    jobApplicationsStats?: Array<{
        _id: string;
        count: number;
    }>;
    assignees?: Array<{
        _id: string;
        assigneeType: string;
        assigneeDetails?: {
            firstName?: string;
            lastName?: string;
            color?: string;
            file?: { url?: string };
            commercialName?: string;
            bossFirstname?: string;
            bossLastname?: string;
            bossPhoto?: string;
        };
    }>;
    views?: number;
    createdDate?: string;
}

interface JobOfferDataTableProps {
    data: JobOffer[];
    onConfirm: (params: { title: string; description: string }) => void;
    loading: boolean;
    isLoadingMore?: boolean;
    onScrollEnd?: () => void;
    setSelectedId: (id: string) => void;
    currentPage: number;
    pageSize: number;
    totalRows: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    containerHeight?: number;
    sortModel?: SortModel[];
    onSortModelChange?: (model: SortModel[]) => void;
}

const JobOfferRowSkeleton = () => (
    <TableRow>
        <TableCell sx={{ width: 58, padding: '6px 8px' }}>
            <Skeleton variant="rectangular" width={24} height={24} />
        </TableCell>
        <TableCell sx={{ minWidth: 300, padding: '6px 8px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Skeleton variant="rectangular" width={16} height={16} />
                <Skeleton variant="text" width="58%" height={20} />
                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                    <Skeleton variant="circular" width={16} height={16} />
                    <Skeleton variant="circular" width={16} height={16} />
                    <Skeleton variant="circular" width={16} height={16} />
                </Box>
            </Box>
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }} align="center">
            <Skeleton variant="text" width={24} height={18} sx={{ mx: 'auto' }} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }} align="center">
            <Skeleton variant="text" width={24} height={18} sx={{ mx: 'auto' }} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }} align="center">
            <Skeleton variant="text" width={24} height={18} sx={{ mx: 'auto' }} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }} align="center">
            <Skeleton variant="text" width={24} height={18} sx={{ mx: 'auto' }} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }} align="center">
            <Skeleton variant="text" width={24} height={18} sx={{ mx: 'auto' }} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }} align="center">
            <Skeleton variant="text" width={24} height={18} sx={{ mx: 'auto' }} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }} align="center">
            <Skeleton variant="text" width={24} height={18} sx={{ mx: 'auto' }} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }} align="center">
            <Skeleton variant="text" width={24} height={18} sx={{ mx: 'auto' }} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }} align="center">
            <Skeleton variant="circular" width={28} height={28} sx={{ mx: 'auto' }} />
        </TableCell>
    </TableRow>
);

const JobOfferDataTable: React.FC<JobOfferDataTableProps> = ({
    data,
    onConfirm,
    loading,
    isLoadingMore,
    onScrollEnd,
    setSelectedId,
    currentPage,
    pageSize,
    totalRows,
    onPageChange,
    onPageSizeChange,
    containerHeight,
    sortModel,
    onSortModelChange,
}) => {
    const [copied, setCopied] = useState(false);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [assignDialogJob, setAssignDialogJob] = useState<JobOffer | null>(null);

    const handleOpenAssign = useCallback((job: JobOffer, e: React.MouseEvent) => {
        e.stopPropagation();
        setAssignDialogJob(job);
    }, []);

    const handleCopy = useCallback(async (value: string, id: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setCopiedId(id);
            setTimeout(() => {
                setCopied(false);
                setCopiedId(null);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    }, []);

    const getStatusCount = useCallback((job: JobOffer, status: JobApplicationStatus): string => {
        return job.jobApplicationsStats?.find(item => item._id === status)?.count?.toString() ?? '0';
    }, []);

    const columns: DataTableColumn<JobOffer>[] = useMemo(() => [
        {
            id: 'title',
            label: <Typography variant="h5" sx={{ fontSize: '14px', fontWeight: 500 }}>Titre de l'offre</Typography>,
            field: 'title',
            width: 400,
            minWidth: 300,
            sortable: true,
            resizable: true,
            sticky: true,
            renderCell: ({ row, isExpanded, toggleExpanded }) => {
                return (
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                justifyContent: 'space-between',
                                minWidth: 0,
                                width: '100%',
                                mb: isExpanded ? 1 : 0,
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <IconButton
                                    size="small"
                                    onClick={toggleExpanded}
                                    sx={{
                                        p: 0.5,
                                        transition: 'transform 0.2s ease-in-out',
                                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(0deg)',
                                        '& svg': {
                                            transition: 'transform 0.2s ease-in-out',
                                        }
                                    }}
                                >
                                    {isExpanded ? <i className="fi fi-br-caret-down" /> : <i className="fi fi-br-caret-right" />}
                                </IconButton>
                                <Typography
                                    component={Link}
                                    to={`/job-offers/${row.jobNumber}/detail`}
                                    variant="h5"
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        flex: 1,
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}>
                                    {row.title}
                                </Typography>
                                <Tooltip title="Attribuer" placement="top">
                                    <Box
                                        onClick={(e) => handleOpenAssign(row, e)}
                                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', ml: '4px' }}
                                    >
                                        {row.assignees && row.assignees.length > 0 ? (
                                            <AvatarGroup max={3} sx={{ display: 'flex', width: '40px', pointerEvents: 'none', '& .MuiAvatar-root': { width: '20px', height: '20px', fontSize: '10px', fontWeight: 600 } }}>
                                                {row.assignees.map((assignee, index) => {
                                                    if (assignee.assigneeDetails) {
                                                        const details = assignee.assigneeDetails;
                                                        const isOrgUser = assignee.assigneeType === 'OrganizationUser';
                                                        const src = isOrgUser ? details.file?.url : details.bossPhoto;
                                                        const initials = isOrgUser
                                                            ? `${details.firstName?.charAt(0) ?? ''}${details.lastName?.charAt(0) ?? ''}`.toUpperCase()
                                                            : (details.commercialName?.charAt(0) ?? details.bossFirstname?.charAt(0) ?? '').toUpperCase();
                                                        const alt = isOrgUser
                                                            ? `${details.firstName} ${details.lastName}`
                                                            : details.commercialName || `${details.bossFirstname} ${details.bossLastname}`;

                                                        const info = getAssigneeDisplayInfo(assignee);
                                                        return (
                                                            <Avatar key={assignee._id || index} alt={alt} src={src} sx={{ width: '16px', height: '16px', fontSize: '7px', fontWeight: 600, bgcolor: info.color }}>
                                                                {!src && initials}
                                                            </Avatar>
                                                        );
                                                    }
                                                    const info = getAssigneeDisplayInfo(assignee);
                                                    return <Avatar key={assignee._id || index} sx={{ width: '16px', height: '16px', fontSize: '7px', fontWeight: 600, bgcolor: info.color }} />;
                                                })}
                                            </AvatarGroup>
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    border: theme => `1px dashed ${theme.palette.text.disabled}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 9,
                                                    color: theme => theme.palette.text.disabled,
                                                    '&:hover': { borderColor: theme => theme.palette.primary.main, color: theme => theme.palette.primary.main },
                                                }}
                                            >
                                                <i className="fi fi-br-plus" style={{ fontSize: 7 }} />
                                            </Box>
                                        )}
                                    </Box>
                                </Tooltip>
                            </Box>

                        </Box>

                        <Collapse in={isExpanded} timeout={300} unmountOnExit>
                            <Box sx={{ pb: 1, pl: 4.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 0.75 }}>
                                    <Avatar
                                        src={row.brand?.brandLogo?.url || row.pro?.brandLogo?.url || '/default-logo.png'}
                                        alt={row.pro?.commercialName || 'Company Logo'}
                                        sx={{ width: '17px', height: '17px' }}
                                    />
                                    <Typography
                                        sx={{
                                            fontWeight: 400,
                                            fontSize: '14px',
                                            lineHeight: 'normal',
                                            color: theme => theme.palette.textSecondary.main,
                                        }}>
                                        {row.pro?.commercialName}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            color: theme => theme.palette.textSecondary.main,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                        }}>
                                        {row.jobNumber}{' '}
                                        <Tooltip open={copied && copiedId === row._id} title="Copy to clipboard" placement="top">
                                            <Box sx={{ width: '10px', height: '12px', display: 'flex', alignItems: 'center' }}>
                                                <CopyToClipboardIcon
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleCopy(row.jobNumber || '', row._id)}
                                                />
                                            </Box>
                                        </Tooltip>
                                        <Box
                                            sx={{
                                                borderRight: theme => `1px dashed ${theme.palette.textSecondary.main}`,
                                                width: '16px',
                                                height: '16px',
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                marginLeft: '8px',
                                                borderRadius: '8px',
                                                padding: '2px 8px',
                                                background: 'rgba(105, 105, 105, 0.30)',
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                color: theme => theme.palette.textSecondary.main,
                                                lineHeight: '16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '2px',
                                            }}>
                                            {row.views ?? 0} <EyeIcon />
                                        </Box>
                                        <Box
                                            sx={{
                                                marginLeft: '8px',
                                                borderRadius: '8px',
                                                padding: '2px 8px',
                                                background: 'rgba(105, 105, 105, 0.30)',
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                color: theme => theme.palette.textSecondary.main,
                                                lineHeight: '16px',
                                            }}>
                                            {row.createdDate ? Math.floor((Date.now() - new Date(row.createdDate).getTime()) / (1000 * 60 * 60 * 24)) : 0} Jours
                                        </Box>
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: '12px', mt: 0.75 }}>
                                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <JobIcon />
                                        <Typography
                                            sx={{
                                                color: theme => theme.palette.textSecondary.main,
                                                fontWeight: 600,
                                                fontSize: '14px',
                                            }}>
                                            {row.contract?.contractType}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            lineHeight: '117.936%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: theme => theme.palette.textSecondary.main,
                                            gap: '4px',
                                        }}>
                                        &#8364;
                                        {formatNumberToK(row?.contract?.guaranteedSalary?.min)}
                                        {row?.contract?.guaranteedSalary?.max ? ` - ${formatNumberToK(row?.contract?.guaranteedSalary?.max)}` : ""} /{" "}
                                        {row?.contract?.isAnnualSalary ? "Annuel" : "Mensuel"} 
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <MapMarker />
                                        <Typography
                                            sx={{
                                                color: theme => theme.palette.textSecondary.main,
                                                fontWeight: 600,
                                                fontSize: '14px',
                                            }}>
                                            {row.pro?.city}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>
                );
            },
        },
        {
            id: 'new',
            label: 'Nouveau',
            width: 80,
            align: 'center',
            sortable: false,
            field: 'jobApplicationsStats' as const,
            renderCell: ({ row }) => (
                <Typography
                    component={Link}
                    to={`/job-offers/${row.jobNumber}/applications?status=${encodeURIComponent(JobApplicationStatus.New)}`}
                    sx={{ fontSize: '14px', fontWeight: 500, textDecoration: 'none', color: 'inherit', '&:hover': { textDecoration: 'underline' } }}
                >
                    {getStatusCount(row, JobApplicationStatus.New)}
                </Typography>
            ),
        },
        {
            id: 'interview',
            label: 'Entretien',
            width: 80,
            align: 'center',
            sortable: false,
            field: 'jobApplicationsStats' as const,
            renderCell: ({ row }) => (
                <Typography
                    component={Link}
                    to={`/job-offers/${row.jobNumber}/applications?status=${encodeURIComponent(JobApplicationStatus.Interview)}`}
                    sx={{ fontSize: '14px', fontWeight: 500, textDecoration: 'none', color: 'inherit', '&:hover': { textDecoration: 'underline' } }}
                >
                    {getStatusCount(row, JobApplicationStatus.Interview)}
                </Typography>
            ),
        },
        {
            id: 'trial',
            label: 'Essai',
            width: 80,
            align: 'center',
            sortable: false,
            field: 'jobApplicationsStats' as const,
            renderCell: ({ row }) => (
                <Typography
                    component={Link}
                    to={`/job-offers/${row.jobNumber}/applications?status=${encodeURIComponent(JobApplicationStatus.Trial)}`}
                    sx={{ fontSize: '14px', fontWeight: 500, textDecoration: 'none', color: 'inherit', '&:hover': { textDecoration: 'underline' } }}
                >
                    {getStatusCount(row, JobApplicationStatus.Trial)}
                </Typography>
            ),
        },
        {
            id: 'offer',
            label: 'Proposition',
            width: 80,
            align: 'center',
            sortable: false,
            field: 'jobApplicationsStats' as const,
            renderCell: ({ row }) => (
                <Typography
                    component={Link}
                    to={`/job-offers/${row.jobNumber}/applications?status=${encodeURIComponent(JobApplicationStatus.Offer)}`}
                    sx={{ fontSize: '14px', fontWeight: 500, textDecoration: 'none', color: 'inherit', '&:hover': { textDecoration: 'underline' } }}
                >
                    {getStatusCount(row, JobApplicationStatus.Offer)}
                </Typography>
            ),
        },
        {
            id: 'probation',
            label: 'Période d\'essai',
            width: 106,
            align: 'center',
            sortable: false,
            field: 'jobApplicationsStats' as const,
            renderCell: ({ row }) => (
                <Typography
                    component={Link}
                    to={`/job-offers/${row.jobNumber}/applications?status=${encodeURIComponent(JobApplicationStatus.ProbationPeriod)}`}
                    sx={{ fontSize: '14px', fontWeight: 500, textDecoration: 'none', color: 'inherit', '&:hover': { textDecoration: 'underline' } }}
                >
                    {getStatusCount(row, JobApplicationStatus.ProbationPeriod)}
                </Typography>
            ),
        },
        {
            id: 'hired',
            label: 'Recruté',
            width: 80,
            align: 'center',
            sortable: false,
            field: 'jobApplicationsStats' as const,
            renderCell: ({ row }) => (
                <Typography
                    component={Link}
                    to={`/job-offers/${row.jobNumber}/applications?status=${encodeURIComponent(JobApplicationStatus.Hired)}`}
                    sx={{ fontSize: '14px', fontWeight: 500, textDecoration: 'none', color: 'inherit', '&:hover': { textDecoration: 'underline' } }}
                >
                    {getStatusCount(row, JobApplicationStatus.Hired)}
                </Typography>
            ),
        },
        {
            id: 'rejected',
            label: 'Refuse',
            width: 80,
            align: 'center',
            sortable: false,
            field: 'jobApplicationsStats' as const,
            renderCell: ({ row }) => (
                <Typography
                    component={Link}
                    to={`/job-offers/${row.jobNumber}/applications?status=${encodeURIComponent(JobApplicationStatus.Rejected)}`}
                    sx={{ fontSize: '14px', fontWeight: 500, textDecoration: 'none', color: 'inherit', '&:hover': { textDecoration: 'underline' } }}
                >
                    {getStatusCount(row, JobApplicationStatus.Rejected)}
                </Typography>
            ),
        },
        {
            id: 'total',
            label: 'Total',
            width: 80,
            align: 'center',
            sortable: true,
            field: 'jobApplicationsStats' as const,
            renderCell: ({ row }) => (
                <Typography
                    component={Link}
                    to={`/job-offers/${row.jobNumber}/applications`}
                    sx={{ fontSize: '14px', fontWeight: 500, textDecoration: 'none', color: 'inherit', '&:hover': { textDecoration: 'underline' } }}
                >
                    {_.sumBy(row.jobApplicationsStats, 'count') || '0'}
                </Typography>
            ),
        },
        {
            id: 'actions',
            label: '',
            width: 80,
            align: 'center',
            sortable: false,
            field: '_id' as const,
            renderCell: ({ row }) => (
                <JobOfferRowActions row={row} />
            ),
        },
    ], [copied, copiedId, handleCopy, getStatusCount, handleOpenAssign]);
    const { containerHeight: measuredContainerHeight, containerRef } = useContainerHeight();
    const resolvedContainerHeight = containerHeight ?? measuredContainerHeight;
    const tableContainerRef = containerHeight == null ? containerRef : undefined;

    return (
        <Box ref={tableContainerRef} sx={{ flex: 1, minHeight: 0 }}>
            {assignDialogJob && (
                <AssignJobOfferDialog
                    open={Boolean(assignDialogJob)}
                    onClose={() => setAssignDialogJob(null)}
                    job={{
                        _id: assignDialogJob._id,
                        title: assignDialogJob.title,
                        proId: assignDialogJob.pro?._id,
                        assignees: assignDialogJob.assignees,
                    }}
                    onSelected={() => {}}
                />
            )}
            <DataTable
                rows={data}
                columns={columns}
                loading={loading}
                isLoadingMore={isLoadingMore}
                onScrollEnd={onScrollEnd}
                checkboxSelection
                height={resolvedContainerHeight || undefined}
                collapsible={true}
                collapseAnchorColumn="title"
                sortModel={sortModel}
                onSortModelChange={onSortModelChange}
                pagination={{
                    currentPage,
                    pageSize,
                    totalRows,
                    onPageChange,
                    onPageSizeChange,
                    pageSizeOptions: [10, 20, 50, 100],
                }}
                stickyHeader
                rowSkeleton={<JobOfferRowSkeleton />}
                noDataComponent={
                    <EmptyState
                        title="Nous n'avons actuellement aucune offre d'emploi"
                        subtitle="Commençons à inviter de nouveaux talents à s'inscrire à Upercut."
                    />
                }
            />
        </Box>
    );
};

export default JobOfferDataTable;
